import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { ChatMessageBubble } from './chat-message-bubble';
import { glideToTop } from './scroll-glide';

export const ChatMessageList = observer(function ChatMessageList() {
  const containerRef = useRef<HTMLDivElement>(null);
  const messages = bibleState.chatMessages;

  const loadingMsg = messages.find((m) => m.isLoading);
  const isLoading = !!loadingMsg;
  const progressCount = loadingMsg?.progressSteps?.length ?? 0;

  const wasLoadingRef = useRef(false);

  // 1) New messages added (user sends) → scroll to bottom
  useEffect(() => {
    scrollToBottom(containerRef.current);
  }, [messages.length]);

  // 2) Progress steps appear → keep bottom in view
  useEffect(() => {
    if (!isLoading || progressCount === 0) return;
    scrollToBottom(containerRef.current);
  }, [progressCount, isLoading]);

  // 3) Loading → done → return the panel to its very top, so the finished
  // answer reads from the beginning and the Scripture accuracy checks panel
  // is on screen (Brian, UAT 2026-09-21 — anchoring on the answer's own
  // first message left the checks below the fold).
  // Double rAF lets the typeset commit and layout settle first (the draft
  // swap changes scrollHeight — measuring too early made the move sudden).
  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = isLoading;
    if (!wasLoading || isLoading) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (containerRef.current) glideToTop(containerRef.current);
      });
    });
  }, [isLoading]);

  return (
    <div ref={containerRef} className="chat-panel-messages">
      {messages.length === 0 && (
        <div className="chat-empty-state">
          <p className="mt-8 text-center text-sm text-stone-400 dark:text-stone-500">
            Click a verse number and click "It is written..."
          </p>
        </div>
      )}
      {messages.map((msg) => (
        <ChatMessageBubble key={msg.id} message={msg} />
      ))}
    </div>
  );
});

function scrollToBottom(container: HTMLDivElement | null): void {
  if (!container) return;
  container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
}
